import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Key,
  Camera,
  AlertTriangle,
  Users,
  Package,
  Car,
  FileText,
  Link2,
  Brain,
  Plug,
} from "lucide-react";
import { SpinLoader } from "../../../components/loader/Loader";
import { getSecurityDashboardStats } from "../../../service/securityService";
import { SecurityDashboardStats } from "../../../types/security_types";
import {
  SECURITY_ACCESS_ZONES,
  SECURITY_CAMERAS,
  SECURITY_INCIDENTS,
  SECURITY_VISITOR_MANAGEMENT,
  SECURITY_PARKING,
  SECURITY_COMPLIANCE_AUDIT,
  SECURITY_DASHBOARD,
} from "../../../constants/modules";
import PermissionWrapper from "../../../components/PermissionWrapper/PermissionWrapper";

const SecurityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SecurityDashboardStats | null>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await getSecurityDashboardStats();
      setStats(data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const dashboardCards = [
    {
      title: "Access Control & Authorization",
      description: "Zones, assignments, and access logs",
      icon: Key,
      path: "/security/access-control",
      module: SECURITY_ACCESS_ZONES,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Surveillance & Monitoring",
      description: "CCTV cameras and monitoring",
      icon: Camera,
      path: "/security/surveillance",
      module: SECURITY_CAMERAS,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Incident & Emergency Management",
      description: "Report and manage security incidents",
      icon: AlertTriangle,
      path: "/security/incidents",
      module: SECURITY_INCIDENTS,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Visitor & Vendor Management",
      description: "Manage visitors and vendors",
      icon: Users,
      path: "/security/visitor-management",
      module: SECURITY_VISITOR_MANAGEMENT,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Asset & Equipment Security",
      description: "Track asset movements and security events",
      icon: Package,
      path: "/security/asset-security",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Staff Safety & Workforce Security",
      description: "Staff safety, threats, and night shift check-ins",
      icon: Users,
      path: "/security/staff-safety",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Parking & Perimeter Security",
      description: "Vehicle management and perimeter control",
      icon: Car,
      path: "/security/parking",
      module: SECURITY_PARKING,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Cyber-Physical Security Integration",
      description: "Unified threat context and physical–digital correlation",
      icon: Link2,
      path: "/security/cyber-physical",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Compliance, Audit & Reporting",
      description:
        "Compliance, audit logs, regulatory exports, and security reports",
      icon: FileText,
      path: "/security/compliance-audit-reporting",
      module: SECURITY_COMPLIANCE_AUDIT,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "AI & Predictive Security",
      description: "Advanced analytics hub; risk scoring in reports",
      icon: Brain,
      path: "/security/ai-predictive",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Security Integration & Interoperability",
      description: "Channels, APIs, and external connector scope",
      icon: Plug,
      path: "/security/integrations",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
    {
      title: "Security Command Center",
      description: "Real-time monitoring and control",
      icon: Shield,
      path: "/security/command-center",
      module: SECURITY_DASHBOARD,
      color: "bg-[#0fa9a6]",
      bgColor: "bg-[#EAFBFA]",
      textColor: "text-[#0fa9a6]",
    },
  ];

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Shield className="w-8 h-8 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Security Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive security management and monitoring system
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <SpinLoader />
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Incidents</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalActiveIncidents}
                </p>
              </div>
              <div className="bg-[#EAFBFA] p-3 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-[#0fa9a6]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Access Zones</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalAccessZones}
                </p>
              </div>
              <div className="bg-[#EAFBFA] p-3 rounded-lg">
                <Key className="w-6 h-6 text-[#0fa9a6]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Cameras</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.activeCameras}/{stats.totalCameras}
                </p>
              </div>
              <div className="bg-[#EAFBFA] p-3 rounded-lg">
                <Camera className="w-6 h-6 text-[#0fa9a6]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Visitors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.activeVisitors}
                </p>
              </div>
              <div className="bg-[#EAFBFA] p-3 rounded-lg">
                <Users className="w-6 h-6 text-[#0fa9a6]" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardCards.map((card) => {
          const Icon = card.icon;
          return (
            <PermissionWrapper
              key={card.path}
              module={card.module}
              action="read"
            >
              <button
                onClick={() => navigate(card.path)}
                className="bg-white rounded-lg shadow-sm p-6 text-left hover:shadow-lg transition-all duration-200 hover:scale-105"
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className={`${card.bgColor} p-3 rounded-lg`}>
                    <Icon className={`w-8 h-8 ${card.textColor}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600">{card.description}</p>
                  </div>
                </div>
              </button>
            </PermissionWrapper>
          );
        })}
      </div>
    </div>
  );
};

export default SecurityDashboard;
